import { zodResolver } from '@hookform/resolvers/zod';
import { useGetNotificationSettings, useUpdateNotificationSettings } from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { AlertCircle, ArrowLeft, Bell, Mail, MessageSquare, Save, Smartphone } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Badge, Button, TextField, Toggle, useToast } from '@/components/ui';

const notificationSettingsSchema = z.object({
  sensorAbsence: z
    .object({
      emailEnabled: z.boolean(),
      smsEnabled: z.boolean(),
      pushEnabled: z.boolean(),
      smsPhoneNumber: z.string().optional(),
    })
    .superRefine((value, ctx) => {
      if (value.smsEnabled && !value.smsPhoneNumber?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Phone number is required when SMS notifications are enabled',
          path: ['smsPhoneNumber'],
        });
      }
    }),
});

type NotificationSettingsFormData = z.infer<typeof notificationSettingsSchema>;

const defaultValues: NotificationSettingsFormData = {
  sensorAbsence: {
    emailEnabled: false,
    smsEnabled: false,
    pushEnabled: false,
    smsPhoneNumber: '',
  },
};

export function NotificationsSettingsPage() {
  const { addToast } = useToast();
  const { data, isLoading, isError, refetch } = useGetNotificationSettings();

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<NotificationSettingsFormData>({
    resolver: zodResolver(notificationSettingsSchema),
    defaultValues,
  });

  useEffect(() => {
    reset({
      sensorAbsence: {
        emailEnabled: data?.sensorAbsence?.emailEnabled ?? false,
        smsEnabled: data?.sensorAbsence?.smsEnabled ?? false,
        pushEnabled: data?.sensorAbsence?.pushEnabled ?? false,
        smsPhoneNumber: data?.sensorAbsence?.smsPhoneNumber ?? '',
      },
    });
  }, [data, reset]);

  const { mutate: updateSettings, isPending: isSaving } = useUpdateNotificationSettings({
    mutation: {
      onSuccess: (updated) => {
        addToast({ message: 'Notification settings saved', variant: 'success' });
        reset({
          sensorAbsence: {
            emailEnabled: updated.sensorAbsence?.emailEnabled ?? false,
            smsEnabled: updated.sensorAbsence?.smsEnabled ?? false,
            pushEnabled: updated.sensorAbsence?.pushEnabled ?? false,
            smsPhoneNumber: updated.sensorAbsence?.smsPhoneNumber ?? '',
          },
        });
        refetch();
      },
      onError: (err: Error) => {
        addToast({
          message: err.message ?? 'Failed to save notification settings',
          variant: 'error',
        });
      },
    },
  });

  const smsEnabled = watch('sensorAbsence.smsEnabled');
  const currentSettings = data?.sensorAbsence;
  const pushDeviceCount = currentSettings?.pushDeviceCount ?? 0;
  const pushReady = currentSettings?.pushReady ?? false;

  const onSubmit = (formData: NotificationSettingsFormData) => {
    updateSettings({
      data: {
        sensorAbsence: {
          emailEnabled: formData.sensorAbsence.emailEnabled,
          smsEnabled: formData.sensorAbsence.smsEnabled,
          pushEnabled: formData.sensorAbsence.pushEnabled,
          smsPhoneNumber: formData.sensorAbsence.smsPhoneNumber?.trim() || undefined,
        },
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to={ROUTES.SETTINGS}
          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-white"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Notifications
          </h1>
          <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
            Choose how sensor absence and recovery alerts are delivered
          </p>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {isError && (
        <div className="rounded-lg bg-red-50 p-6 text-center dark:bg-red-500/10">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="text-sm text-red-600 dark:text-red-400">
            Failed to load notification settings
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm text-blue-500 hover:text-blue-400"
          >
            Try again
          </button>
        </div>
      )}

      {!isLoading && !isError && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="flex items-start gap-3 rounded-lg bg-blue-50 p-4 dark:bg-blue-500/10">
            <Bell className="mt-0.5 h-5 w-5 text-blue-500" />
            <div>
              <p className="text-sm font-medium text-blue-700 dark:text-blue-400">
                Sensor absence alerts
              </p>
              <p className="mt-1 text-sm text-blue-600 dark:text-blue-300/80">
                These settings control alerts when a sensor stops reporting past its configured
                alert threshold, plus the matching recovery message once it starts reporting again.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-white/[0.06] dark:bg-[#18181b]">
            <div className="space-y-5">
              <Controller
                name="sensorAbsence.emailEnabled"
                control={control}
                render={({ field }) => (
                  <Toggle
                    label="Email"
                    description="Send alerts to your account email address"
                    checked={field.value}
                    onChange={field.onChange}
                    layout="switch-right"
                  />
                )}
              />

              <Controller
                name="sensorAbsence.smsEnabled"
                control={control}
                render={({ field }) => (
                  <Toggle
                    label="SMS"
                    description="Send alerts to the phone number below"
                    checked={field.value}
                    onChange={field.onChange}
                    layout="switch-right"
                  />
                )}
              />

              {smsEnabled && (
                <Controller
                  name="sensorAbsence.smsPhoneNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="SMS Phone Number"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      error={errors.sensorAbsence?.smsPhoneNumber?.message}
                      placeholder="+31612345678"
                      hint="E.164 format is recommended"
                    />
                  )}
                />
              )}

              <Controller
                name="sensorAbsence.pushEnabled"
                control={control}
                render={({ field }) => (
                  <Toggle
                    label="Push Notification"
                    description="Send alerts to registered mobile app devices"
                    checked={field.value}
                    onChange={field.onChange}
                    layout="switch-right"
                  />
                )}
              />
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-white/[0.06] dark:bg-[#18181b]">
            <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              Delivery Status
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4 rounded-lg bg-gray-50 px-4 py-3 dark:bg-zinc-800/60">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-gray-500 dark:text-zinc-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">Email</div>
                    <div className="text-sm text-gray-500 dark:text-zinc-500">
                      Uses your signed-in account email address
                    </div>
                  </div>
                </div>
                <Badge variant={watch('sensorAbsence.emailEnabled') ? 'success' : 'default'}>
                  {watch('sensorAbsence.emailEnabled') ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>

              <div className="flex items-center justify-between gap-4 rounded-lg bg-gray-50 px-4 py-3 dark:bg-zinc-800/60">
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-4 w-4 text-gray-500 dark:text-zinc-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">SMS</div>
                    <div className="text-sm text-gray-500 dark:text-zinc-500">
                      {watch('sensorAbsence.smsPhoneNumber')?.trim()
                        ? watch('sensorAbsence.smsPhoneNumber')
                        : 'No phone number configured'}
                    </div>
                  </div>
                </div>
                <Badge variant={watch('sensorAbsence.smsEnabled') ? 'success' : 'default'}>
                  {watch('sensorAbsence.smsEnabled') ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>

              <div className="flex items-center justify-between gap-4 rounded-lg bg-gray-50 px-4 py-3 dark:bg-zinc-800/60">
                <div className="flex items-center gap-3">
                  <Smartphone className="h-4 w-4 text-gray-500 dark:text-zinc-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Push Notification
                    </div>
                    <div className="text-sm text-gray-500 dark:text-zinc-500">
                      {pushDeviceCount === 0
                        ? 'No mobile devices have registered yet'
                        : `${pushDeviceCount} active mobile device${pushDeviceCount === 1 ? '' : 's'}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={watch('sensorAbsence.pushEnabled') ? 'success' : 'default'}>
                    {watch('sensorAbsence.pushEnabled') ? 'Enabled' : 'Disabled'}
                  </Badge>
                  <Badge variant={pushReady ? 'success' : 'warning'}>
                    {pushReady ? 'Ready' : 'Not Ready'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 pt-6 dark:border-white/[0.06]">
            <p className="text-sm text-gray-500 dark:text-zinc-500">
              Push delivery becomes ready after the mobile app registers this account on a device.
            </p>
            <div className="flex items-center gap-3">
              <Link to={ROUTES.SETTINGS}>
                <Button variant="secondary">Cancel</Button>
              </Link>
              <Button type="submit" loading={isSaving} disabled={!isDirty}>
                <Save className="mr-2 h-4 w-4" />
                Save Settings
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
